import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import Nav from "./components/Nav";
import ProtectedRoute from "./components/ProtectedRoute";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import AuthCallback from "./pages/AuthCallback";
import JobsList from "./pages/JobsList";
import JobDetail from "./pages/JobDetail";
import ApplyForm from "./pages/ApplyForm";
import CandidateDashboard from "./pages/CandidateDashboard";
import RecruiterDashboard from "./pages/RecruiterDashboard";
import JobEditor from "./pages/JobEditor";
import ApplicationDetail from "./pages/ApplicationDetail";
import AdminDashboard from "./pages/AdminDashboard";
import AdminPlans from "./pages/AdminPlans";
import Pricing from "./pages/Pricing";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Billing from "./pages/Billing";
import Settings from "./pages/Settings";
import TwoFAVerify from "./pages/TwoFAVerify";
import Pipeline from "./pages/Pipeline";
import Appeal from "./pages/Appeal";
import PendingApproval from "./pages/PendingApproval";
import Disclaimer from "./pages/Disclaimer";
import VerifyEmail from "./pages/VerifyEmail";
import ContactSales from "./pages/ContactSales";
import InviteShare from "./pages/InviteShare";
import CompanyCareers from "./pages/CompanyCareers";
import HRPortal from "./pages/HRPortal";
import PublicTracker from "./pages/PublicTracker";
import AnnouncementBanner from "./components/AnnouncementBanner";
import { Toaster } from "sonner";

const ADMIN_ROLES = ["owner", "moderator"];

function AppRouter() {
  // When the user is viewing a company's branded careers page at /c/{slug},
  // check that company's whitelabel flag. If on, hide the platform Nav +
  // AnnouncementBanner so the page renders with the company's branding alone.
  const location = useLocation();
  const [whitelabel, setWhitelabel] = React.useState(false);
  React.useEffect(() => {
    const m = location.pathname.match(/^\/c\/([^/]+)/);
    if (!m) { setWhitelabel(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const { api } = await import("./lib/api");
        const { data } = await api.get(`/c/${encodeURIComponent(m[1])}/branding`);
        if (!cancelled) setWhitelabel(!!data.whitelabel);
      } catch { /* slug not found — show normal Nav */ }
    })();
    return () => { cancelled = true; };
  }, [location.pathname]);

  return (
    <>
      {!whitelabel && <AnnouncementBanner />}
      {!whitelabel && <Nav />}
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/2fa-verify" element={<TwoFAVerify />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/register" element={<Register />} />
        <Route path="/auth/google" element={<AuthCallback />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/jobs" element={<JobsList />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/jobs/:id" element={<JobDetail />} />
        <Route path="/jobs/:id/apply" element={
          <ProtectedRoute role="candidate"><ApplyForm /></ProtectedRoute>
        } />
        <Route path="/candidate" element={
          <ProtectedRoute role="candidate"><CandidateDashboard /></ProtectedRoute>
        } />
        <Route path="/recruiter" element={
          <ProtectedRoute role="recruiter"><RecruiterDashboard /></ProtectedRoute>
        } />
        <Route path="/billing" element={
          <ProtectedRoute role="recruiter"><Billing /></ProtectedRoute>
        } />
        <Route path="/pipeline" element={
          <ProtectedRoute role="recruiter"><Pipeline /></ProtectedRoute>
        } />
        <Route path="/appeal" element={
          <ProtectedRoute><Appeal /></ProtectedRoute>
        } />
        <Route path="/pending-approval" element={
          <ProtectedRoute><PendingApproval /></ProtectedRoute>
        } />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/disclaimer" element={<Disclaimer />} />
        <Route path="/tracker" element={<PublicTracker />} />
        <Route path="/contact-sales" element={<ContactSales />} />
        <Route path="/invite/:code" element={<InviteShare />} />
        <Route path="/c/:slug" element={<CompanyCareers />} />
        <Route path="/hr" element={<ProtectedRoute><HRPortal /></ProtectedRoute>} />
        <Route path="/settings" element={
          <ProtectedRoute><Settings /></ProtectedRoute>
        } />
        <Route path="/admin" element={
          <ProtectedRoute roles={ADMIN_ROLES}><AdminDashboard /></ProtectedRoute>
        } />
        <Route path="/admin/plans" element={
          <ProtectedRoute role="owner"><AdminPlans /></ProtectedRoute>
        } />
        <Route path="/recruiter/jobs/new" element={
          <ProtectedRoute role="recruiter"><JobEditor /></ProtectedRoute>
        } />
        <Route path="/recruiter/jobs/:id/edit" element={
          <ProtectedRoute role="recruiter"><JobEditor /></ProtectedRoute>
        } />
        <Route path="/recruiter/applications/:id" element={
          <ProtectedRoute role="recruiter"><ApplicationDetail /></ProtectedRoute>
        } />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
